import {
  call, put, takeEvery,
} from 'redux-saga/effects';
import API from '../../api';
import { AsyncActionStatus } from '../enums';
import { generateErrorMessage } from '../helpers';
import { InterviewsActionTypes } from '../actions/types';
import { InterviewsActionCreators, ModalsActionCreators } from '../actions/creators';
import { InterviewStatus } from '../../../../shared/enums';
import downloadFileByURL from '../../helpers/downloadFileByURL';
import { toast } from '../../utilities';
import { validatorsHelper } from '../../../../shared/helpers';

function* getInterviewsInfo(action) {
  try {
    const response = yield call(
      API.Interviews.getInterviewsInfo,
      action.payload.customerId,
      action.payload.projectId,
    );
    yield put(InterviewsActionCreators.getInterviewsInfo[AsyncActionStatus.FULFILLED](
      response.data.interviews,
      response.data.users,
      response.data.package,
      response.data.book,
    ));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(InterviewsActionCreators.getInterviewsInfo[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* sendInterviewInvite(action) {
  try {
    const response = yield call(
      API.Interviews.sendInterviewInvite,
      action.payload.interview,
    );
    yield put(InterviewsActionCreators.sendInterviewInvite[AsyncActionStatus.FULFILLED](response.data.interview));
    yield call(toast.success, t('toastMessages.success.interviewScheduled', { ns: i18next.Namespace.COMMON }));
    yield put(ModalsActionCreators.closeScheduleInterviewModal());
    if (response.data.isFirstInterview) yield put(ModalsActionCreators.openFirstInterviewScheduledModal());
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(InterviewsActionCreators.sendInterviewInvite[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* updateInterview(action) {
  try {
    const response = yield call(
      API.Interviews.updateInterview,
      action.payload.interview,
    );
    yield put(InterviewsActionCreators.updateInterview[AsyncActionStatus.FULFILLED](response.data));

    const { status } = action.payload.interview;

    switch (status) {
      case InterviewStatus.COMPLETED:
      case InterviewStatus.NO_SHOW:
        yield call(toast.success, t('toastMessages.success.interviewsCompleted', { ns: i18next.Namespace.COMMON }));
        yield put(ModalsActionCreators.closeCompleteInterviewModal());
        break;
      case InterviewStatus.ADMIN_CANCELLED:
      case InterviewStatus.EDITOR_CANCELLED:
      case InterviewStatus.WRITER_CANCELLED:
      case InterviewStatus.CUSTOMER_CANCELLED:
        yield call(toast.success, t('toastMessages.success.interviewsCancelled', { ns: i18next.Namespace.COMMON }));
        yield put(ModalsActionCreators.closeCancelInterviewModal());
        yield put(ModalsActionCreators.closeCompleteInterviewModal());
        break;
      case InterviewStatus.CONFIRMED:
        yield call(toast.success, t('toastMessages.success.interviewsConfirmed', { ns: i18next.Namespace.COMMON }));
        yield put(ModalsActionCreators.closeConfirmInterviewModal());
        break;
      default:
        yield call(toast.success, t('toastMessages.success.interviewUpdated', { ns: i18next.Namespace.COMMON }));
        yield put(ModalsActionCreators.closeEditInterviewNoteModal());
    }
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(InterviewsActionCreators.updateInterview[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* attachFileToInterview(action) {
  localStorage.setItem('bookmakerInterviewFileUploading', 'uploading');
  try {
    const response = yield call(
      API.Interviews.attachFileToInterview,
      action.payload.projectId,
      action.payload.interviewId,
      action.payload.interviewFile,
    );
    yield put(InterviewsActionCreators.attachFileToInterview[AsyncActionStatus.FULFILLED](response.data.interview));
    yield call(toast.success, t('toastMessages.success.interviewUpdated', { ns: i18next.Namespace.COMMON }));
    if (validatorsHelper.isValidTypeOfAudioFile(action.payload.interviewFile.type)) {
      localStorage.setItem('lastUploadedFileId', response.data.newFileId);
      yield put(ModalsActionCreators.openTranscribeAudioFileModal());
    }
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(InterviewsActionCreators.attachFileToInterview[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  } finally {
    localStorage.setItem('bookmakerInterviewFileUploading', '');
  }
}

function* deleteFileFromInterview(action) {
  try {
    const response = yield call(
      API.Interviews.deleteFileFromInterview,
      action.payload.interviewId,
      action.payload.interviewFileId,
    );
    yield put(InterviewsActionCreators.deleteFileFromInterview[AsyncActionStatus.FULFILLED](response.data));
    yield call(toast.success, t('toastMessages.success.interviewUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(InterviewsActionCreators.deleteFileFromInterview[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* downloadFileOfInterview(action) {
  try {
    const response = yield call(
      API.Interviews.downloadFileOfInterview,
      action.payload.interviewId,
      action.payload.interviewFileId,
    );
    yield put(InterviewsActionCreators.downloadFileOfInterview[AsyncActionStatus.FULFILLED](response.data));
    downloadFileByURL(response.data.presignedURL, response.data.originalname);
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(InterviewsActionCreators.downloadFileOfInterview[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* toggleAudioFilesRequired(action) {
  try {
    const response = yield call(
      API.Interviews.toggleAudioFilesRequired,
      action.payload.interviewId,
    );
    yield put(InterviewsActionCreators.toggleAudioFilesRequired[AsyncActionStatus.FULFILLED](response.data));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(InterviewsActionCreators.toggleAudioFilesRequired[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* transcribeFileOfInterview(action) {
  try {
    const response = yield call(
      API.Interviews.transcribeFileOfInterview,
      action.payload.interviewFileId,
    );
    yield put(InterviewsActionCreators.transcribeFileOfInterview[AsyncActionStatus.FULFILLED](response.data));
    yield call(toast.success, t('toastMessages.success.audioFileTranscribed', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(InterviewsActionCreators.transcribeFileOfInterview[AsyncActionStatus.REJECTED](message));
    if (/timeout of 600000ms exceeded/.test(message)) {
      yield call(toast.error, t('toastMessages.error.timeoutExceeded', { ns: i18next.Namespace.COMMON }));
    } else yield call(toast.error, message);
  }
}

export default [
  takeEvery(
    InterviewsActionTypes.getInterviewsInfo[AsyncActionStatus.PENDING],
    getInterviewsInfo,
  ),
  takeEvery(
    InterviewsActionTypes.sendInterviewInvite[AsyncActionStatus.PENDING],
    sendInterviewInvite,
  ),
  takeEvery(
    InterviewsActionTypes.updateInterview[AsyncActionStatus.PENDING],
    updateInterview,
  ),
  takeEvery(
    InterviewsActionTypes.attachFileToInterview[AsyncActionStatus.PENDING],
    attachFileToInterview,
  ),
  takeEvery(
    InterviewsActionTypes.deleteFileFromInterview[AsyncActionStatus.PENDING],
    deleteFileFromInterview,
  ),
  takeEvery(
    InterviewsActionTypes.downloadFileOfInterview[AsyncActionStatus.PENDING],
    downloadFileOfInterview,
  ),
  takeEvery(
    InterviewsActionTypes.toggleAudioFilesRequired[AsyncActionStatus.PENDING],
    toggleAudioFilesRequired,
  ),
  takeEvery(
    InterviewsActionTypes.transcribeFileOfInterview[AsyncActionStatus.PENDING],
    transcribeFileOfInterview,
  ),
];
